import React from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  PendingOutlined as PendingIcon,
  DirectionsRunOutlined as OnGoingIcon,
  CheckCircleOutlineOutlined as FinishedIcon,
  CreateOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
} from "@mui/icons-material";
import ActivityDrawer from "./ActivityDrawer";
import { createCustomerPanelUi, CUSTOMER_PANEL_ACCENTS } from "./customerPanelStyles";

import { useTranslation } from "react-i18next"; // Mantener la importación para asegurar la carga de configuraciones de i18n

const Activities = ({
  activities,
  newActivity,
  setNewActivity,
  editActivityId,
  setEditActivityId,
  showActivityForm,
  setShowActivityForm,
  handleActivityChange,
  handleActivitySubmit,
  handleDeleteActivity,
  isDeletionAllowed,
  canAdd,
  COLUMN_WIDTH,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const accent = CUSTOMER_PANEL_ACCENTS.activities;
  const ui = createCustomerPanelUi(theme, accent);
  const safeActivities = Array.isArray(activities) ? activities : [];

  const styles = {
    fondoActividad: {
      ...ui.cardSx,
      width: "100%",
      marginBottom: 1,
      display: "flex",
      justifyContent: "space-between",
      gap: 0.75,
      padding: "0.75rem",
    },
  };
  const panelSx = {
    ...ui.panelSx,
    width: COLUMN_WIDTH,
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
  };
  const primaryButtonSx = ui.addButtonSx;
  const actionIconSx = {
    ...ui.actionIconSx,
  };
  const toSafeText = (value, fallback = "-") => {
    if (value == null || value === "") return fallback;
    return typeof value === "string" ? value : String(value);
  };
  const formatSafeDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? "-"
      : parsed.toLocaleDateString("es-ES");
  };

  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState("");
  const [deleteAction, setDeleteAction] = React.useState(() => () => {});

  const renderStatusTypeIcon = (type) => {
    switch (type) {
      case "Pending":
        return <PendingIcon sx={{ color: "#dc3545" }} />;
      case "On going":
        return <OnGoingIcon sx={{ color: "#3174ad" }} />;
      case "Finished":
        return <FinishedIcon sx={{ color: "#34afe2" }} />;
      default:
        return null;
    }
  };

  const openAddDrawer = () => {
    setNewActivity({
      subject: "",
      activityType: "",
      status: "",
      notes: "",
      activityDate: "",
      gMeetId: "",
    });
    setEditActivityId(null);
    setShowActivityForm(true);
  };

  const openEditDrawer = (activity) => {
    setNewActivity(activity);
    setEditActivityId(activity.id);
    setShowActivityForm(true);
  };

  return (
    <Box sx={panelSx}>
      {/* 🔹 Cabecera fija */}
      <Box sx={{ ...ui.headerSx, pl: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Typography sx={ui.headerEyebrowSx}>
            {t("Activities.Activities")}
          </Typography>
          <Typography sx={ui.headerTitleSx}>
            {safeActivities.length}{" "}
            {safeActivities.length === 1 ? "actividad" : "actividades"}
          </Typography>
        </Box>

        <Button
          onClick={openAddDrawer}
          disabled={!canAdd}
          variant="outlined"
          sx={primaryButtonSx}
        >
          {t("Activities.Add")}
        </Button>
      </Box>

      {/* 🔹 Contenido scrollable */}
      <Box sx={ui.bodySx}>
        {safeActivities.length === 0 ? (
          <Typography sx={ui.emptyStateSx}>
            {t("Activities.NoActivities")}
          </Typography>
        ) : (
          safeActivities.map((activity) => (
            <Box key={activity.id} sx={styles.fondoActividad}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "text.primary",
                    fontSize: "0.9rem",
                    lineHeight: 1.15,
                  }}
                >
                  {toSafeText(activity.subject)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.45,
                    mt: 0.5,
                    mb: 0.55,
                  }}
                >
                  <Box
                    sx={{
                      ...ui.pillSx,
                      fontSize: "0.66rem",
                    }}
                  >
                    {toSafeText(activity.activityType)}
                  </Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.35,
                      ...ui.pillSx,
                      backgroundColor: alpha("#00c2a3", 0.08),
                      fontSize: "0.66rem",
                    }}
                  >
                    {renderStatusTypeIcon(activity.status)}
                    {toSafeText(activity.status)}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.78rem",
                    lineHeight: 1.45,
                  }}
                >
                  {toSafeText(activity.notes)}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.55,
                    fontWeight: 700,
                    color: "text.secondary",
                    fontSize: "0.76rem",
                  }}
                >
                  {formatSafeDate(activity.activityDate)}
                </Typography>
                {activity?.gMeetId && (
                  <Typography variant="body2" sx={{ mt: 0.4, fontSize: "0.74rem" }}>
                    <a href={activity.gMeetId} target="_blank" rel="noreferrer">
                      {toSafeText(activity.gMeetId, "")}
                    </a>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 0.45, ml: 0.35 }}>
                <IconButton
                  onClick={() => openEditDrawer(activity)}
                  aria-label="edit"
                  sx={{
                    ...actionIconSx,
                    borderColor: alpha(accent, 0.24),
                  }}
                >
                  <EditIcon sx={{ color: accent, fontSize: "0.92rem" }} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setConfirmMessage(
                      "Are you sure you want to delete this activity?"
                    );
                    setDeleteAction(() => async () => {
                      try {
                        await handleDeleteActivity(activity.id);
                        setOpenConfirmDialog(false);
                      } catch (err) {
                        console.error("Error deleting activity:", err);
                      }
                    });
                    setOpenConfirmDialog(true);
                  }}
                  aria-label="delete"
                  sx={{
                    ...actionIconSx,
                    borderColor: alpha(theme.palette.error.main, 0.24),
                  }}
                  disabled={!isDeletionAllowed}
                >
                  <DeleteIcon
                    sx={{ color: theme.palette.error.main, fontSize: "0.92rem" }}
                  />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* 🔹 Drawer para editar o crear actividad */}
      <ActivityDrawer
        open={showActivityForm}
        onClose={() => setShowActivityForm(false)}
        editActivityId={editActivityId}
        newActivity={newActivity}
        handleActivityChange={handleActivityChange}
        handleActivitySubmit={handleActivitySubmit}
      />

      {/* 🔹 Confirmación de borrado */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {t("Activities.ConfirmDelete")}{" "}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            {t("Activities.Cancel")}
          </Button>
          <Button onClick={deleteAction} color="primary" autoFocus>
            {t("Activities.Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Activities;
